import React, { useState, useEffect } from 'react';
import {
    getBlockedSlots,
    blockSlot,
    unblockSlot,
    getBookedSlots
} from '../services/bookingService';
import { toast } from 'react-toastify';

const RED = '#B22222';
const DARK_RED = '#8B0000';
const GOLD = '#FFD700';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TIME_OPTIONS = (() => {
    const list = [];
    for (let h = 6; h <= 21; h++) {
        for (const m of ['00', '30']) {
            const value = `${String(h).padStart(2, '0')}:${m}`;
            const period = h >= 12 ? 'PM' : 'AM';
            const h12 = h % 12 === 0 ? 12 : h % 12;
            list.push({ value, label: `${h12}:${m} ${period}` });
        }
    }
    return list;
})();

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${DAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

const time12h = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    const hour = parseInt(h);
    const period = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${h12}:${m} ${period}`;
};

/**
 * ============================================================
 * CALENDAR BLOCK - poojari apna samay band kare
 * ============================================================
 *
 * ⚠️ YE KYUN CHAHIYE:
 *
 * Poojari ko OFFLINE pooja bhi milti hai - mandir se,
 * jaan-pehchan se. Pehle wo app mein wo din block nahi kar
 * sakta tha.
 *
 * Natija: user us din book karta, aur phir poojari ya to reject
 * karta ya accept hi nahi karta (aur booking 12 ghante mein
 * auto-cancel ho jaati). DONO mein USER ka bharosa tootta -
 * aur wahi launch pe sabse bada risk hai.
 *
 * ⚠️ isAvailable TOGGLE SE ALAG:
 *
 * Wo poori tarah band karta hai (chhutti, bimari). Poojari use
 * dabane mein hichkichata hai kyunki kaam bilkul ruk jaata hai.
 * Ye ek din ya kuch ghante ke liye hai. Dono chahiye.
 *
 * ⚠️ FORM KA KRAM: DATE PEHLE, phir full-day/hours.
 *
 * Ulta karte to poojari "Full day" chunta, phir date daalta,
 * aur TAB pata chalta ki us din booking hai aur full day ho hi
 * nahi sakta - use wapas jaake type badalna padta.
 *
 * Date pehle hone se booking turant dikh jaati hai aur "Full
 * day" apne aap band ho jaata hai. Galti ka mauka hi nahi.
 */
const CalendarBlockSection = ({ poojariId }) => {

    const [blocks, setBlocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [removingId, setRemovingId] = useState(null);

    const [date, setDate] = useState('');
    const [fullDay, setFullDay] = useState(true);
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [reason, setReason] = useState('');

    // Us din ki pehle se maujood bookings
    const [dayBookings, setDayBookings] = useState([]);
    const [checkingDay, setCheckingDay] = useState(false);

    // ⚠️ Aaj se pehle ki date chun hi na sake - HTML ka min
    // attribute. Backend bhi rokta hai, par yahan rokne se
    // poojari ko error khana hi nahi padta.
    const today = new Date().toISOString().split('T')[0];

    useEffect(() => {
        if (poojariId) {
            fetchBlocks();
        }
        // eslint-disable-next-line
    }, [poojariId]);

    const fetchBlocks = async () => {
        setLoading(true);
        try {
            const data = await getBlockedSlots(poojariId);
            setBlocks(Array.isArray(data) ? data : []);
        } catch (err) {
            setBlocks([]);
        } finally {
            setLoading(false);
        }
    };

    /**
     * ⚠️ Date chunte hi us din ki bookings laate hain.
     *
     * Isi se "Full day" band hota hai jab booking maujood ho.
     * Bina iske poojari poora form bharta aur "Block" dabane pe
     * backend se error khata - jo bura experience hai.
     */
    const handleDateChange = async (e) => {
        const d = e.target.value;
        setDate(d);
        setDayBookings([]);

        if (!d) return;

        setCheckingDay(true);
        try {
            const slots = await getBookedSlots(poojariId, d);
            const list = Array.isArray(slots) ? slots : [];

            // ⚠️ FULL_DAY wale entry hata do - wo hamara apna
            // block hai, booking nahi. Use dikhane se poojari
            // confuse hoga.
            const onlyBookings = list.filter(s => s !== 'FULL_DAY');
            setDayBookings(onlyBookings);

            // ⚠️ Booking hai to full day apne aap band, aur
            // "Some hours" pe switch
            if (onlyBookings.length > 0 && fullDay) {
                setFullDay(false);
            }
        } catch (err) {
            setDayBookings([]);
        } finally {
            setCheckingDay(false);
        }
    };

    const handleSave = async () => {

        if (!date) {
            return toast.warning('Please pick a date');
        }

        if (!fullDay) {
            if (!startTime || !endTime) {
                return toast.warning('Enter both start and end time');
            }
            if (endTime <= startTime) {
                return toast.warning('End time must be after start time');
            }
        }

        setSaving(true);
        try {
            await blockSlot(
                poojariId,
                date,
                fullDay ? null : startTime,
                fullDay ? null : endTime,
                reason.trim() || null);

            toast.success('Date blocked');
            resetForm();
            fetchBlocks();
        } catch (err) {
            toast.error(err.response?.data?.message
                || 'Could not block this date');
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async (id) => {
        setRemovingId(id);
        try {
            await unblockSlot(id);
            toast.success('Unblocked');
            fetchBlocks();
        } catch (err) {
            toast.error('Could not unblock');
        } finally {
            setRemovingId(null);
        }
    };

    const resetForm = () => {
        setShowForm(false);
        setDate('');
        setFullDay(true);
        setStartTime('');
        setEndTime('');
        setReason('');
        setDayBookings([]);
    };

    const label = {
        color: '#888',
        fontSize: '11px',
        marginBottom: '4px',
        display: 'block'
    };

    const input = {
        borderColor: GOLD,
        borderRadius: '8px',
        fontSize: '14px'
    };

    if (loading) {
        return null;
    }

    return (
        <div className="p-3 mb-3"
            style={{
                backgroundColor: 'white',
                border: `1px solid ${GOLD}`,
                borderRadius: '12px'
            }}>

            <div className="d-flex justify-content-between align-items-center mb-1">
                <h6 className="mb-0"
                    style={{ color: DARK_RED, fontWeight: 700 }}>
                    Block my calendar
                </h6>
                {!showForm && (
                    <button
                        type="button"
                        className="btn btn-link p-0"
                        style={{
                            color: RED,
                            fontSize: '13px',
                            fontWeight: 600,
                            textDecoration: 'none'
                        }}
                        onClick={() => setShowForm(true)}>
                        + Add
                    </button>
                )}
            </div>

            <p className="mb-2"
                style={{ fontSize: '11px', color: '#888' }}>
                Days you are busy with offline poojas. Users cannot
                book you at these times.
            </p>

            {showForm && (
                <div className="p-3 mb-3"
                    style={{
                        backgroundColor: '#FFF5F5',
                        borderRadius: '10px'
                    }}>

                    {/* ===== 1. DATE ===== */}
                    <label style={label}>1. Which date</label>
                    <input
                        type="date"
                        className="form-control mb-2"
                        style={input}
                        value={date}
                        min={today}
                        onChange={handleDateChange}
                        disabled={saving} />

                    {/* ⚠️ Us din ki bookings - date chunte hi dikhti
                        hain. Isi se poojari ko pata chalta hai ki
                        full day ho sakta hai ya nahi. */}
                    {checkingDay && (
                        <p style={{ fontSize: '11px', color: '#888' }}>
                            Checking your bookings...
                        </p>
                    )}

                    {!checkingDay && dayBookings.length > 0 && (
                        <div className="p-2 mb-2"
                            style={{
                                backgroundColor: '#FFF8E1',
                                border: '1px solid #FFB74D',
                                borderRadius: '8px'
                            }}>
                            <p className="mb-0"
                                style={{
                                    fontSize: '11px',
                                    color: '#8A5300'
                                }}>
                                You have a booking at{' '}
                                {dayBookings.join(', ')} on this date.
                                Full day cannot be blocked.
                            </p>
                        </div>
                    )}

                    {/* ===== 2. FULL DAY / SOME HOURS ===== */}
                    <label style={label}>2. What to block</label>
                    <div className="d-flex gap-2 mb-2">
                        <button
                            type="button"
                            className="btn flex-fill"
                            style={{
                                backgroundColor: fullDay ? RED : 'white',
                                color: fullDay ? GOLD : DARK_RED,
                                border: `1px solid ${GOLD}`,
                                borderRadius: '8px',
                                fontSize: '13px',
                                opacity: dayBookings.length > 0 ? 0.4 : 1
                            }}
                            onClick={() => setFullDay(true)}
                            disabled={saving || dayBookings.length > 0}>
                            Full day
                        </button>
                        <button
                            type="button"
                            className="btn flex-fill"
                            style={{
                                backgroundColor: !fullDay ? RED : 'white',
                                color: !fullDay ? GOLD : DARK_RED,
                                border: `1px solid ${GOLD}`,
                                borderRadius: '8px',
                                fontSize: '13px'
                            }}
                            onClick={() => setFullDay(false)}
                            disabled={saving}>
                            Some hours
                        </button>
                    </div>

                    {!fullDay && (
                        <div className="d-flex gap-2 mb-2">
                            <div style={{ flex: 1 }}>
                                <label style={label}>From</label>
                                <select
                                    className="form-select"
                                    style={input}
                                    value={startTime}
                                    onChange={(e) =>
                                        setStartTime(e.target.value)}
                                    disabled={saving}>
                                    <option value="">Select</option>
                                    {TIME_OPTIONS.map(t => (
                                        <option key={t.value}
                                            value={t.value}>
                                            {t.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={label}>To</label>
                                <select
                                    className="form-select"
                                    style={input}
                                    value={endTime}
                                    onChange={(e) =>
                                        setEndTime(e.target.value)}
                                    disabled={saving}>
                                    <option value="">Select</option>
                                    {TIME_OPTIONS.map(t => (
                                        <option key={t.value}
                                            value={t.value}>
                                            {t.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {/* ===== 4. NOTE ===== */}
                    {/* ⚠️ Ye note SIRF POOJARI ko dikhta hai. User ko
                        booked slots mein sirf time jaata hai, wajah
                        nahi - wo doosre yajman ki niji baat hai. */}
                    <label style={label}>
                        Note (only you can see this)
                    </label>
                    <input
                        type="text"
                        className="form-control mb-3"
                        style={input}
                        value={reason}
                        maxLength={200}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Griha pravesh at Sharma ji"
                        disabled={saving} />

                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn flex-fill fw-bold"
                            style={{
                                backgroundColor: RED,
                                color: GOLD,
                                border: 'none',
                                borderRadius: '9px',
                                fontSize: '14px'
                            }}
                            onClick={handleSave}
                            disabled={saving}>
                            {saving ? 'Blocking...' : 'Block this date'}
                        </button>
                        <button
                            type="button"
                            className="btn"
                            style={{
                                border: `1px solid ${GOLD}`,
                                borderRadius: '9px',
                                color: '#888',
                                fontSize: '14px'
                            }}
                            onClick={resetForm}
                            disabled={saving}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* ===== BLOCKED DATES KI LIST ===== */}
            {blocks.length === 0 ? (
                !showForm && (
                    <p className="mb-0"
                        style={{ fontSize: '12px', color: '#aaa' }}>
                        No blocked dates
                    </p>
                )
            ) : (
                <div style={{ borderTop: '1px solid #F0E0E0' }}>
                    {blocks.map((b) => (
                        <div key={b.id}
                            className="d-flex justify-content-between align-items-center"
                            style={{
                                padding: '9px 0',
                                borderBottom: '1px solid #FAF0F0'
                            }}>
                            <div>
                                <div style={{
                                    fontSize: '13px',
                                    color: '#333'
                                }}>
                                    {formatDate(b.blockedDate)}
                                </div>
                                <div style={{
                                    fontSize: '11px',
                                    color: '#888'
                                }}>
                                    {b.fullDay
                                        ? 'Full day'
                                        : `${time12h(b.startTime)} - ${time12h(b.endTime)}`}
                                    {b.reason && ` \u00B7 ${b.reason}`}
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-link p-0"
                                style={{
                                    color: '#bbb',
                                    fontSize: '12px',
                                    textDecoration: 'none'
                                }}
                                onClick={() => handleRemove(b.id)}
                                disabled={removingId === b.id}>
                                {removingId === b.id
                                    ? '...'
                                    : 'Remove'}
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CalendarBlockSection;